import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/contact", label: "Contact" },
];

const LogoMark = () => (
  <span className="inline-flex items-center gap-3">
    <svg width="32" height="32" viewBox="0 0 40 40" fill="none" aria-hidden>
      <path d="M4 4h13v6H10v7H4V4z" stroke="#4dd7ee" strokeWidth="2" strokeLinejoin="round" />
      <path d="M36 4H23v6h7v7h6V4z" stroke="#4dd7ee" strokeWidth="2" strokeLinejoin="round" />
      <path d="M4 36h13v-6H10v-7H4v13z" stroke="#4dd7ee" strokeWidth="2" strokeLinejoin="round" />
      <path d="M36 36H23v-6h7v-7h6v13z" stroke="#4dd7ee" strokeWidth="2" strokeLinejoin="round" />
    </svg>
    <span className="flex flex-col items-center leading-none">
      <span
        className="font-bold tracking-[0.28em] text-[13px] px-2 py-1 rounded-md"
        style={{
          color: "#4dd7ee",
          background: "rgba(77,215,238,0.08)",
          border: "1px solid rgba(77,215,238,0.28)",
        }}
      >
        FOUR SQUARE
      </span>
      <span
        className="mt-1 text-[9.5px] tracking-[0.22em] uppercase"
        style={{ color: "rgba(155, 205, 230, 0.72)" }}
        data-testid="nav-subtitle"
      >
        Network · Cloud · AI · Communication
      </span>
    </span>
  </span>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="sticky top-0 z-40"
      data-testid="site-navbar"
      style={{
        backdropFilter: "blur(14px)",
        background: "rgba(5, 11, 26, 0.65)",
        borderBottom: "1px solid rgba(77,215,238,0.10)",
      }}
    >
      <div className="fs-container flex items-center justify-between h-[70px]">
        <Link to="/" data-testid="nav-logo" className="shrink-0" onClick={() => setOpen(false)}>
          <LogoMark />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) => `fs-nav-link ${isActive ? "active" : ""}`}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:+918681833444"
            className="hidden lg:inline-flex items-center gap-2 text-[13px] text-[color:var(--text-dim)] hover:text-[color:var(--cyan-bright)] transition-colors"
            data-testid="nav-phone"
          >
            <Phone size={15} />
            +91 86818 33444
          </a>
          <Link to="/contact" data-testid="nav-cta" className="hidden sm:inline-flex fs-btn-primary text-sm">
            Talk to an Expert
          </Link>
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-[color:var(--text)]"
            style={{ border: "1px solid rgba(77,215,238,0.2)", background: "rgba(77,215,238,0.06)" }}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden"
            data-testid="nav-mobile-menu"
            style={{
              borderTop: "1px solid rgba(77,215,238,0.10)",
              background: "rgba(5, 11, 26, 0.92)",
              backdropFilter: "blur(14px)",
            }}
          >
            <div className="fs-container py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-3 rounded-md text-sm tracking-[0.2em] uppercase transition-colors ${
                      isActive
                        ? "text-[color:var(--cyan-bright)] bg-[rgba(77,215,238,0.08)]"
                        : "text-[color:var(--text-dim)] hover:text-[color:var(--cyan-bright)] hover:bg-[rgba(77,215,238,0.04)]"
                    }`
                  }
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-3 flex flex-col sm:hidden gap-2">
                <Link to="/contact" onClick={() => setOpen(false)} className="fs-btn-primary text-sm justify-center">
                  Talk to an Expert
                </Link>
                <a
                  href="tel:+918681833444"
                  className="fs-btn-outline-soft text-sm justify-center"
                >
                  <Phone size={14} /> +91 86818 33444
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
